import React, { useState, useEffect } from 'react';
import styles from './switch-room-modal.module.scss';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import Select, { ValueType } from 'react-select';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { getAllUnitTypes } from '../../../../../shared/services/data/unit-type.data';
import { UnitTypeUiModelResponse } from '../../../../../shared/ui-models/unit-type.ui-model';
import { ErrorInfo, isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { getModuleName } from '../../../../shared/services/reservation.service';

export interface SwitchRoomModalProps {
  config: {
    showModal: boolean;
  };
  data: {
    reservation: ReservationDetailsUiModel | undefined;
    switchRoomLoading: boolean;
  };
  eventHandlers: {
    closeHandler: () => void;
    switchRoomHandler: (unitTypeId: number, roomId: string) => void;
  };
}

/**
 * functional component SwitchRoomModal
 * @param {SwitchRoomModalProps}
 */
export const SwitchRoomModal: React.FC<SwitchRoomModalProps> = ({
  config,
  eventHandlers,
  data
}) => {
  const [newUnitTypeId, setNewUnitTypeId] = useState<FormElementData<number>>({
    value: 0,
    isValid: true
  });
  const [newRoomId, setNewRoomId] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });

  const [unitTypes, setUnitTypes] = useState<
    { unitTypeId: number; unitTypeName: string; roomName: string; roomId: string }[]
  >([]);
  const [selectedValue, setSelectedValue] = useState<{
    value: string;
    label: string;
    unitTypeId?: number;
  } | null>(null);

  const isMobileView = useWindowResizer();

  const hasRoomChanged = newRoomId.value !== '' && newRoomId.value !== data.reservation?.roomId;

  // Get current room details
  const currentRoom = unitTypes?.find((ut) => ut.roomId === data.reservation?.roomId);

  useEffect(() => {
    if (data.reservation && data.reservation.id) {
      getAllUnitTypes(getModuleName(), 0, -1, {}).then(
        (response: UnitTypeUiModelResponse | ErrorInfo) => {
          if (!isErrorInfo(response)) {
            const propertyUnitType = response.unitList;
            //   .filter(
            //   (unitType) => unitType.propertyId === data.reservation?.propertyId
            // );
            let unitTypesSelectList: {
              unitTypeId: number;
              unitTypeName: string;
              roomName: string;
              roomId: string;
            }[] = [];
            propertyUnitType?.forEach((unitType) => {
              if (unitType.units && unitType.units.length > 0) {
                const unitTypeMap = unitType.units.map((unit) => {
                  return {
                    unitTypeId: unitType.id || 0,
                    unitTypeName: unitType.name || '',
                    roomName: unit.shortName || '',
                    roomId: unit.unitId
                  };
                });
                unitTypesSelectList = [...unitTypesSelectList, ...unitTypeMap];
              }
            });
            setUnitTypes(unitTypesSelectList);
          }
        }
      );
    }
  }, [data.reservation]);

  const handleReservationRoomSWitch = () => {
    eventHandlers.switchRoomHandler(newUnitTypeId.value, newRoomId.value);
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.closeHandler();
        }
      }}
    >
      <div className={styles['switch-room-modal']}>
        {/* Header */}
        <div className={styles['switch-room-modal__header']}>
          <h2 className={styles['switch-room-modal__title']}>Switch Reservation Room</h2>
          <p className={styles['switch-room-modal__subtitle']}>
            Select a new room for this reservation. The current booking will be updated to the new
            location.
          </p>
        </div>

        {/* Room Selection */}
        <div className={styles['switch-room-modal__content']}>
          {/* Current Room Display */}
          {currentRoom && (
            <>
              <div className={styles['switch-room-modal__current']}>
                <div className={styles['switch-room-modal__current-label']}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 2L2 6V14H6V10H10V14H14V6L8 2Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Current Room
                </div>
                <div className={styles['switch-room-modal__current-details']}>
                  <div className={styles['switch-room-modal__current-room']}>
                    {currentRoom.roomName}
                  </div>
                  <div className={styles['switch-room-modal__current-type']}>
                    {currentRoom.unitTypeName}
                  </div>
                </div>
              </div>

              {/* Transition Arrow */}
              <div className={styles['switch-room-modal__arrow']}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 5V19M12 19L19 12M12 19L5 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </>
          )}

          <label htmlFor="room-select" className={styles['switch-room-modal__label']}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14 6.5L8 2L2 6.5V14H6V10C6 9.44772 6.44772 9 7 9H9C9.55228 9 10 9.44772 10 10V14H14V6.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Select New Room
          </label>

          {unitTypes && unitTypes.length > 0 ? (
            <Select
              inputId="room-select"
              value={selectedValue}
              options={unitTypes.map((unitType) => ({
                value: unitType.roomId,
                label: `${unitType.roomName} (${unitType.unitTypeId}) - ${unitType.unitTypeName}`,
                unitTypeId: unitType.unitTypeId
              }))}
              placeholder="Select a room..."
              isClearable
              isSearchable
              isDisabled={data.switchRoomLoading}
              classNamePrefix="react-select"
              className="react-select-container"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 99999 })
              }}
              onChange={(
                value: ValueType<{ value: string; label: string; unitTypeId?: number }, false>
              ) => {
                setSelectedValue(value as any);
                setNewRoomId({
                  value: value ? (value.value as string) : '',
                  isValid: !!value
                });
                setNewUnitTypeId({
                  value: (value as any)?.unitTypeId || 0,
                  isValid: !!value
                });
              }}
            />
          ) : (
            <div className={styles['switch-room-modal__loading']}>Loading available rooms...</div>
          )}

          {newRoomId.value && (
            <div className={styles['switch-room-modal__selected']}>
              <div className={styles['switch-room-modal__selected-header']}>
                <span className={styles['switch-room-modal__selected-label']}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.5 4.5L6 12L2.5 8.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  New Room Selected
                </span>
              </div>
              <div className={styles['switch-room-modal__selected-details']}>
                <div className={styles['switch-room-modal__selected-room']}>
                  {unitTypes?.find((ut) => ut.roomId === newRoomId.value)?.roomName}
                </div>
                <div className={styles['switch-room-modal__selected-type']}>
                  {unitTypes?.find((ut) => ut.roomId === newRoomId.value)?.unitTypeName}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={styles['switch-room-modal__actions']}>
          <Button
            onClick={() => {
              eventHandlers.closeHandler();
            }}
            type={ButtonType.primaryInverted}
            tracking={{ event: 'record_edit_cancelled', entity: 'reservation', method: 'close' }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Cancel
          </Button>
          <Button
            loading={data.switchRoomLoading}
            disabled={
              !newRoomId.isValid ||
              !newUnitTypeId.isValid ||
              newRoomId.value === '' ||
              newUnitTypeId.value === 0 ||
              data.switchRoomLoading ||
              !hasRoomChanged
            }
            onClick={handleReservationRoomSWitch}
            tracking={{
              event: 'record_saved',
              entity: 'reservation',
              action: 'update',
              outcome: 'success'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            {data.switchRoomLoading ? 'Switching...' : 'Switch Rooms'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
